import React from 'react';
import { ButtonV2, Flex, FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { useUnlockController } from './UnlockForm.controller';
import { StyledFormStack, StyledTitleStack, StyledHeaderStack, StyledInputsStack, StyledContent, styles } from './UnlockForm.styles';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, display4ToHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useTranslation } from '@shared/core';

interface UnlockFormProps {
  eventId: string;
  onUnlockSuccess: () => void;
  onShowAskHost: () => void;
}

export const UnlockForm: React.FC<UnlockFormProps> = ({ eventId, onUnlockSuccess, onShowAskHost }) => {
  const { formik, loading, inputError, onSignInHandler } = useUnlockController({ eventId, onUnlockSuccess });
  const { tq } = useTranslation('guestUnlock');

  return (
    <StyledContent
      __css={styles.content}
      data-testid="unlock-form"
    >
      <StyledHeaderStack>
        <StyledTitleStack>
          <GuestSiteTypographyOverride override={display4ToHeadingOverride}>
            <TextV2 typographyVariant="hed5">{tq`Private Event`}</TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
            <TextV2
              typographyVariant="body2"
              color="mono10"
            >{tq`Use the password shared by your host to see event details. `}</TextV2>
          </GuestSiteTypographyOverride>
          <Flex
            alignItems="center"
            gap={3}
          >
            <TextV2
              typographyVariant="label3"
              color="mono8"
            >{tq`Need it?`}</TextV2>
            <ButtonV2
              variant="ghost"
              intent="neutral"
              typographyVariant="label3"
              color="mono8"
              width="fit-content"
              height="fit-content"
              padding={0}
              borderBottom="1px solid"
              borderBottomColor="mono5"
              borderBottomLeftRadius="0px"
              borderBottomRightRadius="0px"
              onClick={onShowAskHost}
            >
              {tq`Ask the Host`}
            </ButtonV2>
          </Flex>
        </StyledTitleStack>
      </StyledHeaderStack>
      <StyledFormStack
        spacing={6}
        __css={styles.formStack}
      >
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsStack>
            <FormControl label={tq`Event Password`}>
              <InputV2
                placeholder={tq`eg. xy4686`}
                isInvalid={(inputError && formik.touched.password) || undefined}
                enableFocusOnMount
                isDisabled={loading}
                {...formik.getFieldProps('password')}
              />
            </FormControl>
          </StyledInputsStack>
          <ButtonV2
            shape="rounded"
            width={'100%'}
            intent="neutral"
            type="submit"
            loading={loading}
          >
            {tq`View Event`}
          </ButtonV2>

          <Flex
            justifyContent="center"
            marginTop={6}
          >
            <ButtonV2
              variant="ghost"
              intent="neutral"
              typographyVariant="button2"
              onClick={onSignInHandler}
              width="fit-content"
              height="fit-content"
              padding={0}
              borderBottom="1px solid"
              borderBottomColor="mono5"
              borderBottomLeftRadius="0px"
              borderBottomRightRadius="0px"
            >
              {tq`Sign In`}
            </ButtonV2>
          </Flex>
        </form>
      </StyledFormStack>
    </StyledContent>
  );
};
